import { useListSearchState } from "common/hooks";
import { useFetchQuery } from "common/queries/hooks";

import { Paths } from "client/routes";
import { useProductFilterOptionState, useProductSearchQuery } from "client/products/hooks";
import { collectionFilterScope } from "client/products/hooks/constants";
import { SelectedProductFilters } from "client/products/types";

import { StorefrontCollection } from "../types";
import { SEARCH_DEFAULTS, getSelectedFilters, hasAnyFilterValue } from "../utils";
import { keys, urls } from "./constants";

export const useStorefrontCollectionQuery = (
  slug: string,
  { enabled = true }: { enabled?: boolean } = {},
) =>
  useFetchQuery<StorefrontCollection>(keys.detail(slug), {
    fetch: { url: urls.storefrontCollection(slug) },
    config: { enabled: enabled && !!slug },
  });

export const useCollectionSearch = (collection: StorefrontCollection) => {
  const [search, updateSearch] =
    useListSearchState<Paths["productCollections"]["detail"]>(SEARCH_DEFAULTS);

  const filterOptionsScope = collectionFilterScope(collection.slug);
  const selectedFilters = getSelectedFilters(search, collection.show_filters);

  const {
    data: products,
    isLoading,
    isRefetching,
    error,
  } = useProductSearchQuery(
    {
      q: "",
      page: Number(search.page) || 1,
      page_size: search.page_size,
      sort: search.sort,
      collection: collection.slug,
      ...selectedFilters,
    },
    { filterOptionsScope },
  );

  const { data: filterOptions } = useProductFilterOptionState(filterOptionsScope);

  return {
    products,
    isLoading,
    isRefetching,
    error,
    filterOptions: filterOptions?.filters,
    selectedFilters,
    hasSelectedFilters: hasAnyFilterValue(selectedFilters),
    applyFilters: (next: Partial<SelectedProductFilters>) => updateSearch({ ...next, page: 1 }),
    goToPage: (nextPage: number) => {
      updateSearch({ page: nextPage });
      // Matches ProductSearchPage; without it page 2 opens scrolled to the bottom.
      window.scrollTo(0, 0);
    },
  };
};
